import { useCallback, useMemo } from 'react'
import compact from 'lodash/fp/compact'
import uniq from 'lodash/fp/uniq'

import {
  labelCodeIdVar,
  labelCodeVar,
  labelTitleVar,
  spaceIdVar,
} from 'modulesCommon/store'

import { resetAllFilters } from 'shared/utils/resetAllFilters'
import { useCountry } from 'modulesCommon/serviceHooks'
import { useGetSpacesQuery, GetSpacesQuery } from 'modulesCommon/graphql/queries/getSpaces.types'
import { getCountryByCode } from 'shared/utils/country'
import { ArrayElement } from 'shared/types'

type TSpace = ArrayElement<NonNullable<GetSpacesQuery['getSpaces']>>

interface ISubtitle {
  name: string
  alias: string
}
interface ISpaceOption {
  value: string
  label: string
  subtitle: ReadonlyArray<ISubtitle>
}

export const SPACE_ID_PERSIST_KEY = 'space'

export function spaceOptionAdapter(data: TSpace): ISpaceOption {
  return {
    value: data?.id ?? '',
    label: data?.name ?? '',
    subtitle: (data?.labels ?? []).reduce<ReadonlyArray<ISubtitle>>(
      (acc, label) => typeof label.alias === 'string' ? [
        ...acc,
        {
          alias: label.alias,
          name: label.name
        },
      ] : acc,
      []
    )
  }
}

type TOptionAdapter<T> = (data: TSpace) => T

export const useSpaces = <Option = ISpaceOption>(optionAdapter: TOptionAdapter<Option> = spaceOptionAdapter as TOptionAdapter<Option>) => {
  const { data, loading, error } = useGetSpacesQuery()

  const { setCountry } = useCountry()

  const byId = useMemo<Record<string, TSpace>>(
    () => (data?.getSpaces ?? []).reduce((acc, val) => ({ ...acc, [val.id || '']: val }), {}),
    [data?.getSpaces],
  )

  const getSpaceById = useCallback(
    (id) => id ? byId[id] : undefined,
    [byId],
  )

  const spacesOptions = useMemo<Option[]>(
    () => (data?.getSpaces || []).map(optionAdapter),
    [data?.getSpaces, optionAdapter],
  )

  const getCountryOptions = useCallback(
    (id?: string): { label: string; value: string }[] => {
      if (typeof id !== 'string') return []
      return uniq(
        (byId[id]?.labels ?? []).reduce<string[]>(
          (a, v) => a.concat(compact(v?.country_codes ?? [])),
          []
        )
      )
        .map(value => ({ label: getCountryByCode(value) || '', value }))
    },
    [byId],
  )

  // TODO: Should be reconsidering
  const setSpace = useCallback<(space_id: string, country_code?: string) => void>(
    (space_id, country_code) => {
      const space = getSpaceById(space_id)
      if (typeof space !== 'undefined') {
        resetAllFilters()
        spaceIdVar(space_id)
        localStorage.setItem(SPACE_ID_PERSIST_KEY, space_id)
        if (typeof country_code !== 'undefined') {
          setCountry(country_code)
        } else {
          const countries = getCountryOptions(space_id)
          countries[0]?.value && setCountry(countries[0].value)
        }
        labelCodeIdVar('')
        labelTitleVar('')
        labelCodeVar('')
      }
    },
  [getCountryOptions, getSpaceById, setCountry]
  )

  return {
    getSpaceById,
    getCountryOptions,
    setSpace,
    spacesList: data?.getSpaces ?? [],
    spacesOptions,
    spacesLoading: loading,
    spacesError: error
  }
}